/**
 * GuidePage — individual guide from data/guides.js (iter 174).
 *
 * B2B counterpart to ArticlePage. Same reading experience — Markdown
 * body, FAQ accordion, sticky sidebar CTA — but with post-acute-care
 * platform CTAs instead of family-side ("Get matched") CTAs.
 *
 * Route: /guides/:slug
 */
import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowRight, ArrowLeft, Clock, ShieldCheck, ClipboardCheck } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { getGuide } from '../data/guides';
import { articleSchema, faqSchema, breadcrumbSchema } from '../lib/seoSchemas';

const mdComponents = {
  h2: ({ node, ...p }) => <h2 className="cn-display text-2xl md:text-3xl text-cn-text mt-12 mb-4" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-display font-semibold text-xl text-cn-text mt-8 mb-3" {...p} />,
  p: ({ node, ...p }) => <p className="text-cn-text-muted leading-relaxed text-base md:text-lg mb-5" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc ml-5 space-y-2.5 mb-6 marker:text-cn-primary" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal ml-5 space-y-2.5 mb-6 marker:text-cn-primary marker:font-semibold" {...p} />,
  li: ({ node, ...p }) => <li className="text-cn-text-muted leading-relaxed pl-1" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-cn-text" {...p} />,
  a: ({ node, ...p }) => <a className="text-cn-primary underline underline-offset-2 hover:text-cn-accent" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
};

export default function GuidePage() {
  const { slug } = useParams();
  const g = getGuide(slug);
  if (!g) return <Navigate to="/guides" replace />;

  const path = `/guides/${g.slug}`;
  const related = (g.related || []).map(getGuide).filter(Boolean);
  const schema = [
    articleSchema({
      title: g.title, description: g.metaDescription, slug: g.slug,
      path,
      image: g.heroImage, datePublished: g.datePublished, dateModified: g.dateModified,
    }),
    g.faqs?.length ? faqSchema(g.faqs) : null,
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Guides', path: '/guides' },
      { name: g.title, path },
    ]),
  ].filter(Boolean);

  const prettyDate = new Date(g.dateModified || g.datePublished).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="guide-page">
      <Seo
        title={g.metaTitle}
        description={g.metaDescription}
        path={path}
        image={g.heroImage}
        keywords={g.keywords}
        schema={schema}
      />
      <PublicNavbar />

      <div className="cn-container pt-6">
        <nav aria-label="Breadcrumb" className="text-xs text-cn-text-muted" data-testid="guide-breadcrumb">
          <Link to="/" className="hover:text-cn-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/guides" className="hover:text-cn-primary">Guides</Link>
          <span className="mx-2">/</span>
          <span className="text-cn-text/70">{g.category}</span>
        </nav>
      </div>

      <article className="cn-container pt-8 pb-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="cn-eyebrow !mb-0 text-cn-accent">{g.category}</span>
            <span className="text-xs text-cn-text-muted inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {g.readMinutes} min read
            </span>
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl text-cn-text mb-5 leading-tight" data-testid="guide-h1">
            {g.title}
          </h1>
          <p className="text-lg text-cn-text-muted leading-relaxed">{g.excerpt}</p>
          <div className="mt-5 text-sm text-cn-text-muted">
            By <span className="text-cn-text font-medium">{g.reviewer?.name || 'The CaraLoom Clinical Team'}</span>
            {g.reviewer?.role && <span> · {g.reviewer.role}</span>}
            <span> · Updated {prettyDate}</span>
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 max-w-3xl" data-testid="guide-body">
            <ReactMarkdown components={mdComponents}>{g.body}</ReactMarkdown>

            {g.faqs?.length > 0 && (
              <div className="mt-14">
                <h2 className="cn-display text-2xl md:text-3xl text-cn-text mb-6">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {g.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`guide-faq-${i}`}>
                      <AccordionTrigger className="text-left text-cn-text font-medium">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-cn-text-muted leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="cn-card rounded-3xl p-6 bg-cn-soft/40" data-testid="guide-cta-panel">
                <h3 className="font-display font-semibold text-lg text-cn-text mb-2">See CaraLoom in action</h3>
                <p className="text-sm text-cn-text-muted mb-4 leading-relaxed">
                  A 30-minute demo of discharge planning and post-acute referral management on CaraLoom. No PHI required.
                </p>
                <Link to="/contact?type=demo" className="cn-btn cn-btn-primary w-full justify-center" data-testid="guide-cta-demo">
                  Request a demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/discharge-planning-software" className="cn-btn cn-btn-ghost w-full justify-center mt-2" data-testid="guide-cta-platform">
                  See the platform
                </Link>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="cn-trust-badge"><ShieldCheck className="w-3.5 h-3.5" /> HIPAA-grade</span>
                  <span className="cn-trust-badge"><ClipboardCheck className="w-3.5 h-3.5" /> CMS-ready</span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-12">
          <Link to="/guides" className="text-sm text-cn-primary font-semibold inline-flex items-center gap-1" data-testid="guide-back-link">
            <ArrowLeft className="w-4 h-4" /> All guides
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="cn-container pb-20" data-testid="guide-related">
          <h2 className="cn-display text-2xl text-cn-text mb-6">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/guides/${r.slug}`}
                className="cn-card cn-card-hover rounded-3xl block group p-5"
                data-testid={`guide-related-${r.slug}`}
              >
                <span className="cn-eyebrow !mb-1 text-cn-accent">{r.category}</span>
                <h3 className="font-semibold text-cn-text leading-snug group-hover:text-cn-primary">{r.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
